import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  Inject,
  inject,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { LanguageService } from '../../../shared/services/language.service';
import { NavItem } from '../../models/nav-item.model';
import { NavigationService } from '../../services/navigation.service';
import { SUPPORTED_LANGS, SupportedLang } from '../../../shared/config/localized-paths';

@Component({
  selector: 'xbh-header-utility',
  templateUrl: './header-utility.component.html',
  styleUrls: ['./header-utility.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderUtilityComponent implements OnInit {
  isLangDropdownOpen = false;
  isLoginDropdownOpen = false;

  /** Tracks the selected language locally so the button reflects the user's
   * click immediately — before the async translate.use() resolves. */
  selectedLang = 'en';

  @ViewChild('langTrigger') private readonly langTriggerRef!: ElementRef<HTMLButtonElement>;
  @ViewChild('langMenu') private readonly langMenuRef!: ElementRef<HTMLElement>;
  @ViewChild('loginTrigger') private readonly loginTriggerRef!: ElementRef<HTMLButtonElement>;
  @ViewChild('loginMenu') private readonly loginMenuRef!: ElementRef<HTMLElement>;

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly hostEl: ElementRef<HTMLElement>,
    private readonly cdr: ChangeDetectorRef,
    private readonly languageService: LanguageService,
    private readonly navigationService: NavigationService,
    private readonly renderer: Renderer2,
    private readonly translate: TranslateService,
    @Inject(DOCUMENT) private readonly document: Document,
  ) { }

  ngOnInit(): void {
    this.selectedLang = this.translate.getCurrentLang() || this.translate.getFallbackLang();
    this.languageService.initialize(this.renderer);

    // Keep selectedLang in sync and re-render when language changes
    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(event => {
        this.selectedLang = event.lang;
        this.cdr.markForCheck();
      });
  }

  get currentLangLabel(): string {
    return this.selectedLang === 'es' ? 'Español' : 'English';
  }

  get currentLang(): string {
    return this.selectedLang;
  }

  get loginItems(): NavItem[] {
    return this.navigationService.loginItems;
  }

  // ── Language dropdown ────────────────────────────────────────────────────────
  openLangDropdown(): void {
    this.isLangDropdownOpen = true;
    this.isLoginDropdownOpen = false;
  }

  toggleLangDropdown(): void {
    this.isLangDropdownOpen = !this.isLangDropdownOpen;
    this.isLoginDropdownOpen = false;
  }

  closeLangDropdown(): void {
    this.isLangDropdownOpen = false;
  }

  onLangKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggleLangDropdown();
      if (!this.isLangDropdownOpen) { return; }
      setTimeout(() => this.focusFirstLangItem(), 0);
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.openLangDropdown();
      setTimeout(() => this.focusFirstLangItem(), 0);
    } else if (event.key === 'Escape') {
      this.closeLangDropdown();
    }
  }

  onLangMenuKeydown(event: KeyboardEvent): void {
    const items = this.getLangMenuItems();
    const currentIndex = items.indexOf(this.document.activeElement as HTMLElement);

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      const next = items[currentIndex + 1] ?? items[0];
      next?.focus();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (currentIndex > 0) {
        items[currentIndex - 1]?.focus();
      } else {
        this.closeLangDropdown();
        this.langTriggerRef?.nativeElement?.focus();
      }
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.closeLangDropdown();
      this.langTriggerRef?.nativeElement?.focus();
    }
  }

  onLanguageClick(key: string): void {
    if (!SUPPORTED_LANGS.includes(key as SupportedLang)) { return; }
    this.selectedLang = key;
    this.closeLangDropdown();
    this.languageService.setLanguage(key);
  }

  onLangDropdownFocusout(event: FocusEvent): void {
    const relatedTarget = event.relatedTarget as HTMLElement | null;
    if (!this.document.querySelector('.myce-lang-dropdown')?.contains(relatedTarget)) {
      // Defer so the pending (click) fires before *ngIf destroys the dropdown DOM.
      setTimeout(() => this.closeLangDropdown());
    }
  }

  private focusFirstLangItem(): void {
    this.getLangMenuItems()[0]?.focus();
  }

  private getLangMenuItems(): HTMLElement[] {
    return Array.from(
      this.langMenuRef?.nativeElement?.querySelectorAll<HTMLElement>('.myce-lang-option') ?? [],
    );
  }

  // ── Login dropdown ──────────────────────────────────────────────────────────
  openLoginDropdown(): void {
    this.isLoginDropdownOpen = true;
    this.isLangDropdownOpen = false;
  }

  toggleLoginDropdown(): void {
    this.isLoginDropdownOpen = !this.isLoginDropdownOpen;
    this.isLangDropdownOpen = false;
  }

  closeLoginDropdown(): void {
    this.isLoginDropdownOpen = false;
  }

  onLoginKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggleLoginDropdown();
      if (!this.isLoginDropdownOpen) { return; }
      setTimeout(() => this.focusFirstLoginItem(), 0);
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.openLoginDropdown();
      setTimeout(() => this.focusFirstLoginItem(), 0);
    } else if (event.key === 'Escape') {
      this.closeLoginDropdown();
    }
  }

  onLoginMenuKeydown(event: KeyboardEvent): void {
    const items = this.getLoginMenuItems();
    const currentIndex = items.indexOf(this.document.activeElement as HTMLElement);

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      const next = items[currentIndex + 1] ?? items[0];
      next?.focus();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (currentIndex > 0) {
        items[currentIndex - 1]?.focus();
      } else {
        this.closeLoginDropdown();
        this.loginTriggerRef?.nativeElement?.focus();
      }
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.closeLoginDropdown();
      this.loginTriggerRef?.nativeElement?.focus();
    }
  }

  private focusFirstLoginItem(): void {
    this.getLoginMenuItems()[0]?.focus();
  }

  private getLoginMenuItems(): HTMLElement[] {
    return Array.from(
      this.loginMenuRef?.nativeElement?.querySelectorAll<HTMLElement>('.myce-utility-bar__dropdown-link') ?? [],
    );
  }

  onLoginDropdownFocusout(event: FocusEvent): void {
    const relatedTarget = event.relatedTarget as HTMLElement | null;
    if (!this.document.querySelector('.myce-login-dropdown')?.contains(relatedTarget)) {
      this.closeLoginDropdown();
    }
  }

  // ── Global escape key ────────────────────────────────────────────────────────

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    this.isLangDropdownOpen = false;
    this.isLoginDropdownOpen = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isLangDropdownOpen && !this.isLoginDropdownOpen) { return; }

    const target = event.target as Node | null;
    if (target && !this.hostEl.nativeElement.contains(target)) {
      this.isLangDropdownOpen = false;
      this.isLoginDropdownOpen = false;
    }
  }

  trackByLabel(_index: number, item: NavItem): string {
    return item.labelEn;
  }
}
